// src/components/legal/HealthCheckTermsOfService.js
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Container = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 3rem 2rem;
    line-height: 1.8;
    color: #333;
`;

const Header = styled.header`
    text-align: center;
    margin-bottom: 3rem;
    padding-bottom: 2rem;
    border-bottom: 2px solid #eee;
`;

const Title = styled.h1`
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: #14B8A6;
`;

const UpdateDate = styled.p`
    color: #666;
    font-size: 1rem;
`;

const BackLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    color: #14B8A6;
    text-decoration: none;
    margin-bottom: 2rem;
    transition: opacity 0.3s;

    &:hover {
        opacity: 0.8;
    }

    &::before {
        content: '\2190';
        margin-right: 0.5rem;
    }
`;

const Section = styled.section`
    margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #444;
`;

const Paragraph = styled.p`
    margin-bottom: 1rem;
    text-align: justify;
    white-space: pre-wrap;
`;

const WarningBox = styled.div`
    background: #FFF3E0;
    border-left: 4px solid #FF9800;
    padding: 1.5rem;
    border-radius: 5px;
    margin: 2rem 0;
`;

const InfoBox = styled.div`
    background: #E0F7F5;
    padding: 1.5rem;
    border-radius: 10px;
    margin: 2rem 0;
    white-space: pre-wrap;
`;

const HealthCheckTermsOfService = () => {
    return (
        <Container>
            <BackLink to="/healthcheck">헬스체크로 돌아가기</BackLink>

            <Header>
                <Title>헬스체크 이용약관</Title>
                <UpdateDate>최종 수정일: 2026년 3월 16일</UpdateDate>
            </Header>

            <WarningBox>
                <strong>중요 안내:</strong> 헬스체크는 건강검진 결과를 AI로 분석하여 쉽게 이해할 수 있도록 돕는 정보 제공 서비스이며, 전문적인 의료 서비스나 의학적 진단을 대체하지 않습니다. 건강에 대한 우려가 있으시면 반드시 전문 의료진과 상담하시기 바랍니다.
            </WarningBox>

            <Section>
                <SectionTitle>1. 서비스 이용 동의</SectionTitle>
                <Paragraph>
                    본 이용약관(이하 "약관")은 마인드월드(MindWorld, 이하 "회사")가 제공하는 헬스체크(Health Check) 모바일 애플리케이션(이하 "서비스") 이용에 관한 조건과 절차를 규정합니다.

                    서비스를 이용함으로써 귀하는:
                    • 본 약관에 동의하며 구속력을 인정합니다
                    • 만 14세 이상임을 확인합니다
                    • 법적 구속력이 있는 계약 체결 능력이 있음을 보증합니다
                    • 거주 지역의 모든 관련 법률을 준수할 것을 동의합니다

                    본 약관에 동의하지 않으시면 서비스를 이용하실 수 없습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>2. 서비스 설명</SectionTitle>
                <Paragraph>
                    헬스체크는 건강검진 결과를 AI로 분석하여 쉽게 이해할 수 있도록 돕는 건강 정보 제공 앱입니다.

                    주요 서비스:
                    • 건강검진 결과지 OCR 스캔
                    • AI 기반 쉬운 해석
                    • 항목별 참고범위 비교
                    • 생활습관 개선 팁
                    • 연도별 추이 비교
                    • AI 건강 상담 챗봇

                    서비스 제한사항:
                    • 의료 서비스가 아니며 의학적 진단/처방을 제공하지 않음
                    • 검진 결과 해석은 참고용이며 전문의 상담을 대체하지 않음
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>3. 계정 생성 및 보안</SectionTitle>
                <Paragraph>
                    계정 생성:
                    • 정확하고 완전한 정보를 제공해야 합니다
                    • 허위 정보 제공 시 계정이 정지될 수 있습니다
                    • 1인 1계정 원칙을 준수해야 합니다

                    계정 보안:
                    • 비밀번호를 안전하게 관리할 책임이 있습니다
                    • 계정 무단 사용을 발견하면 즉시 회사에 통지해야 합니다
                    • 계정 양도, 판매, 대여는 금지됩니다
                    • 타인의 계정을 무단으로 사용할 수 없습니다

                    계정 정지/삭제:
                    • 약관 위반 시 사전 통지 없이 계정이 정지될 수 있습니다
                    • 장기간 미사용 계정은 사전 통지 후 삭제될 수 있습니다
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>4. 사용자 행동 규칙</SectionTitle>
                <Paragraph>
                    금지 행위:
                    • 불법적이거나 유해한 목적으로 서비스 사용
                    • 타인의 개인정보 수집 또는 도용
                    • 바이러스, 악성코드 유포
                    • 서비스 해킹, 리버스 엔지니어링
                    • 자동화된 수단으로 서비스 접근
                    • 허위 정보 또는 오해의 소지가 있는 콘텐츠 생성
                    • 타인을 괴롭히거나 위협하는 행위
                    • 지적재산권 침해

                    콘텐츠 가이드라인:
                    • 업로드하는 모든 콘텐츠에 대한 책임은 사용자에게 있습니다
                    • 부적절한 콘텐츠는 삭제될 수 있습니다
                    • 회사는 사용자 콘텐츠를 검토할 권리가 있습니다
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>5. 지적재산권</SectionTitle>
                <Paragraph>
                    회사의 권리:
                    • 서비스의 모든 콘텐츠, 기능, 디자인은 회사의 소유입니다
                    • 상표, 로고, 서비스 마크는 회사의 자산입니다
                    • 무단 복제, 수정, 배포는 금지됩니다

                    사용자 콘텐츠:
                    • 사용자가 생성한 콘텐츠의 소유권은 사용자에게 있습니다
                    • 회사에 콘텐츠 사용에 대한 비독점적 라이선스를 부여합니다
                    • 서비스 개선 및 마케팅 목적으로 사용될 수 있습니다
                    • 익명화된 데이터는 연구 목적으로 사용될 수 있습니다

                    제3자 콘텐츠:
                    • 일부 콘텐츠는 제3자 라이선스 하에 제공됩니다
                    • 해당 라이선스 조건을 준수해야 합니다
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>6. 유료 서비스 및 환불</SectionTitle>
                <Paragraph>
                    구독 서비스:
                    • 무료 (연 1회 분석)
                    • 건당 결제 (2,900원/회)
                    • 월간 구독 (4,900원/월)
                    • 연간 구독 (29,000원/년)
                    • 자동 갱신되며 취소 전까지 요금이 청구됩니다
                    • 구독 기간 중 취소해도 기간 만료까지 이용 가능합니다

                    결제:
                    • Google Play Store를 통해 처리됩니다
                    • 해당 스토어의 결제 정책이 적용됩니다
                    • 가격은 사전 통지 후 변경될 수 있습니다

                    환불 정책:
                    • 구독 시작 후 7일 이내 전액 환불 가능
                    • 기술적 문제로 서비스 이용이 불가능한 경우 환불
                    • 스토어 정책에 따라 환불이 제한될 수 있습니다
                    • 환불 요청: mind-wellness@naver.com
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>7. 면책 조항</SectionTitle>
                <Paragraph>
                    의료 면책:
                    • 서비스는 의료 서비스를 대체하지 않습니다
                    • 의학적 진단이나 처방을 제공하지 않습니다
                    • 의료적 조언은 전문 의료진에게 받으시기 바랍니다
                    • 건강 문제 발견 시 즉시 전문가에게 상담하십시오

                    서비스 제공:
                    • 서비스는 "있는 그대로" 제공됩니다
                    • 특정 목적에의 적합성을 보증하지 않습니다
                    • 오류나 중단 없는 서비스를 보장하지 않습니다
                    • 서비스 이용 결과에 대해 보증하지 않습니다

                    제3자 서비스:
                    • 제3자 링크나 서비스에 대해 책임지지 않습니다
                    • 제3자 서비스 이용 시 해당 약관이 적용됩니다
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>8. 책임 제한</SectionTitle>
                <Paragraph>
                    회사의 책임:
                    • 법이 허용하는 최대 범위 내에서 책임이 제한됩니다
                    • 간접적, 결과적, 특별, 징벌적 손해에 대해 책임지지 않습니다
                    • 최대 책임 한도는 지난 12개월간 지불한 서비스 요금입니다

                    손해배상:
                    • 귀하의 약관 위반으로 인한 손해를 배상해야 합니다
                    • 제3자 클레임으로부터 회사를 면책해야 합니다
                    • 법적 비용을 포함한 모든 손해를 배상해야 합니다

                    불가항력:
                    • 천재지변, 전쟁, 테러, 정부 조치 등으로 인한 서비스 중단
                    • 통제 불가능한 상황에 대해 책임지지 않습니다
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>9. 개인정보 보호</SectionTitle>
                <Paragraph>
                    개인정보 처리:
                    • 별도의 개인정보처리방침에 따라 처리됩니다
                    • GDPR, CCPA 등 국제 규정을 준수합니다
                    • 건강검진 결과 데이터는 특별히 보호됩니다

                    데이터 보안:
                    • 업계 표준 보안 조치를 적용합니다
                    • 암호화 및 접근 제한을 통해 보호합니다
                    • 데이터 유출 시 관련 법령에 따라 통지합니다

                    사용자 권리:
                    • 개인정보 열람, 수정, 삭제 권리
                    • 데이터 이동권
                    • 처리 거부권
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>10. 약관 변경</SectionTitle>
                <Paragraph>
                    변경 절차:
                    • 중요 변경사항은 30일 전 사전 고지
                    • 경미한 변경은 7일 전 고지
                    • 앱 내 알림, 이메일로 통지

                    변경 후 이용:
                    • 변경된 약관 시행 후 서비스 이용 시 동의로 간주
                    • 동의하지 않을 경우 서비스 이용을 중단해야 합니다
                    • 유료 구독자는 현재 구독 기간까지 이전 약관 적용 가능

                    이전 버전:
                    • 요청 시 이전 버전의 약관 제공 가능
                    • mind-wellness@naver.com으로 문의
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>11. 서비스 종료</SectionTitle>
                <Paragraph>
                    사용자에 의한 종료:
                    • 언제든지 계정 삭제 가능
                    • 앱 내 설정 또는 고객센터 통해 요청
                    • 미사용 구독 기간에 대한 환불은 스토어 정책 따름

                    회사에 의한 종료:
                    • 약관 위반 시 즉시 종료 가능
                    • 서비스 전체 종료 시 30일 전 통지
                    • 데이터 백업 기회 제공

                    종료 후 처리:
                    • 개인정보는 법적 의무 기간 후 삭제
                    • 익명화된 데이터는 보관 가능
                    • 일부 조항은 종료 후에도 유효
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>12. 분쟁 해결</SectionTitle>
                <Paragraph>
                    준거법:
                    • 대한민국 법률이 적용됩니다
                    • 국제사법 규정은 배제됩니다

                    분쟁 해결 절차:
                    1. 고객센터를 통한 우호적 해결 시도
                    2. 한국소비자원 등 조정기관 활용
                    3. 민사소송 (관할: 서울중앙지방법원)

                    집단소송 포기:
                    • 개별적으로만 클레임 제기 가능
                    • 집단소송 참여 권리 포기
                    • 중재 절차는 개별적으로 진행

                    예외사항:
                    • 지적재산권 침해는 즉시 법원 제소 가능
                    • 긴급 구제가 필요한 경우 법원 신청 가능
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>13. 기타 조항</SectionTitle>
                <Paragraph>
                    완전한 합의:
                    • 본 약관이 당사자 간 완전한 합의를 구성합니다
                    • 이전의 모든 합의나 양해를 대체합니다

                    분리가능성:
                    • 일부 조항이 무효여도 나머지는 유효합니다
                    • 무효 조항은 유사한 유효 조항으로 대체됩니다

                    권리 포기:
                    • 권리 미행사가 권리 포기를 의미하지 않습니다
                    • 서면으로만 권리 포기가 유효합니다

                    양도:
                    • 사용자는 권리를 양도할 수 없습니다
                    • 회사는 사업 양도 시 권리 양도 가능합니다

                    언어:
                    • 한국어 버전이 우선합니다
                    • 번역본과 충돌 시 한국어 버전 적용
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>14. 연락처</SectionTitle>
                <Paragraph>
                    회사 정보:
                    • 회사명: 마인드월드(MindWorld)
                    • 대표자: 윤우재
                    • 주소: 경기도 양주시 덕계로 137
                    • 사업자등록번호: 504-43-28680

                    고객 지원:
                    • 이메일: mind-wellness@naver.com
                    • 응답 시간: 영업일 기준 48시간 이내
                    • 운영 시간: 평일 09:00-18:00 (KST)

                    기타 문의:
                    • 사업 제휴: mind-wellness@naver.com
                    • 법적 문의: mind-wellness@naver.com
                </Paragraph>
            </Section>

            <InfoBox>
                <SectionTitle>약관 정보</SectionTitle>
                <Paragraph>
                    • 최초 작성일: 2026년 3월 16일
                    • 최종 수정일: 2026년 3월 16일
                    • 시행일: 2026년 3월 16일
                    • 버전: 1.0
                </Paragraph>
            </InfoBox>
        </Container>
    );
};

export default HealthCheckTermsOfService;
